import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { googleSignIn } from '@/lib/api'

// Minimal typing for the Google Identity Services global (loaded via a script in the
// app layout). Only the bits we use are declared.
interface GoogleCredentialResponse {
  credential: string
}
interface GoogleIdApi {
  initialize(opts: { client_id: string; callback: (r: GoogleCredentialResponse) => void }): void
  renderButton(parent: HTMLElement, opts: Record<string, unknown>): void
}
declare global {
  interface Window {
    google?: { accounts: { id: GoogleIdApi } }
  }
}

// Google's official GIS button is an <iframe> and can only be themed from a fixed set of
// presets (none match our cyan/navy palette). So we render the real button transparently
// on top to capture the click — preserving the secure id_token flow — and show our own
// .btn-secondary ghost pill underneath, styled to sit naturally next to the cyan CTA.
const GOOGLE_BUTTON_WIDTH = 320 // px; the visible pill (w-80) is sized to match for click overlap.

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  )
}

// Renders the Google sign-in button when a client id is configured (server injects it as
// a <meta name="google-client-id">). On credential, exchanges the id_token via our
// googleSignIn mutation. Renders nothing if Google isn't configured/loaded.
export default function GoogleSignInButton({ onError }: { onError: (msg: string) => void }) {
  const queryClient = useQueryClient()
  const ref = useRef<HTMLDivElement>(null)
  // Only show this block (and the "or" divider) once Google is actually configured and
  // rendered — otherwise we'd leave a dangling divider with nothing under it.
  const [rendered, setRendered] = useState(false)

  useEffect(() => {
    const clientId = document.querySelector('meta[name="google-client-id"]')?.getAttribute('content')
    const api = window.google?.accounts?.id
    if (!clientId || !api || !ref.current) return

    api.initialize({
      client_id: clientId,
      callback: async (response) => {
        try {
          const { user, errors } = await googleSignIn(response.credential)
          if (user) {
            // Seed the cache from the mutation result so the app re-gates immediately. The
            // `me` query starts disabled (no token at mount), so invalidate alone wouldn't
            // refetch it in-session — only a page refresh would pick the user up.
            queryClient.setQueryData(['me'], user)
          } else {
            onError(errors[0] ?? 'Google sign-in failed.')
          }
        } catch {
          onError('Google sign-in failed.')
        }
      },
    })
    // The real button is invisible (overlaid), so its theme is irrelevant — we only size
    // it to match our visible pill so the click target lines up.
    api.renderButton(ref.current, { theme: 'filled_black', size: 'large', width: GOOGLE_BUTTON_WIDTH })
    setRendered(true)
  }, [queryClient, onError])

  return (
    <>
      <div
        className="flex items-center gap-3 py-2 text-xs text-gray-400"
        style={{ display: rendered ? undefined : 'none' }}
      >
        <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        or
        <span className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
      </div>

      {/* Visible ghost pill + transparent real Google button on top to catch the click. */}
      <div
        className="relative mx-auto h-11 w-80"
        style={{ display: rendered ? undefined : 'none' }}
      >
        <button type="button" tabIndex={-1} className="btn-secondary pointer-events-none absolute inset-0 w-full">
          <GoogleMark />
          Sign in with Google
        </button>
        <div ref={ref} className="absolute inset-0 flex items-center justify-center opacity-0" />
      </div>
    </>
  )
}
